import { KeyboardEvent, ReactNode, useCallback, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

export const SECTION_PARAM = 'view';

// The first section is the default: it leaves the URL bare, and a link to a section the viewer
// has no tab for opens it instead.
export function useSectionParam<S extends string>(
  sections: readonly S[],
): [S, (section: S) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(SECTION_PARAM);
  const defaultSection = sections[0];
  const section = sections.find((candidate) => candidate === requested) ?? defaultSection;
  const setSection = useCallback(
    (next: S) =>
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next === defaultSection) {
            params.delete(SECTION_PARAM);
          } else {
            params.set(SECTION_PARAM, next);
          }
          return params;
        },
        { replace: true },
      ),
    [defaultSection, setSearchParams],
  );
  return [section, setSection];
}

export type SectionPanel<S extends string> = {
  section: S;
  label: string;
  render: () => ReactNode;
};

// WCAG 1.4.11: the pink-700 fill is 2.3:1 on the dark surface, so the pink-300 bar, 3.4:1 on the
// fill and 7.9:1 on the surface, is what marks the chosen tab there.
const CHOSEN_TAB_CLASS = 'bg-pink-700 text-white shadow-[inset_0_-3px_0_0_theme(colors.pink.300)]';
const OTHER_TAB_CLASS =
  'text-gray-300 hover:bg-slate-700 light:text-gray-700 light:hover:bg-gray-200';

const TAB_KEY_STEP: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_index, count) => count - 1,
};

// WAI-ARIA tabs with manual activation: the arrow keys only move focus, so passing over a tab
// never mounts its panel or fires its queries. With keepHiddenPanelsMounted, a panel mounts the
// first time its tab is chosen and then stays mounted, hidden, so a half-filled bid form survives
// a look at the pools; without it, a hidden panel unmounts, which is what closes an IRM loan view.
// With a single panel there is nothing to choose, so no tab list renders.
export function SectionTabs<S extends string>({
  label,
  idPrefix,
  className = '',
  keepHiddenPanelsMounted = false,
  panels,
  active,
  onChange,
}: {
  label: string;
  idPrefix: string;
  className?: string;
  keepHiddenPanelsMounted?: boolean;
  panels: SectionPanel<S>[];
  active: S;
  onChange: (section: S) => void;
}) {
  const tabRefs = useRef(new Map<S, HTMLButtonElement>());
  const mounted = useRef(new Set<S>());
  const available = new Set(panels.map(({ section }) => section));
  mounted.current.forEach((section) => {
    if (!keepHiddenPanelsMounted || !available.has(section)) mounted.current.delete(section);
  });
  mounted.current.add(active);

  if (panels.length === 1) {
    return <>{panels[0].render()}</>;
  }

  const tabId = (section: S) => `${idPrefix}-tab-${section}`;
  const panelId = (section: S) => `${idPrefix}-panel-${section}`;

  const moveFocus = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = TAB_KEY_STEP[event.key];
    if (!step) return;
    event.preventDefault();
    tabRefs.current.get(panels[step(index, panels.length)].section)?.focus();
  };

  return (
    <>
      <div
        role="tablist"
        aria-label={label}
        className={`inline-flex rounded border border-slate-400 light:border-gray-600 ${className}`}
      >
        {panels.map(({ section, label: tabLabel }, index) => (
          <button
            key={section}
            ref={(element) => {
              if (element) tabRefs.current.set(section, element);
              else tabRefs.current.delete(section);
            }}
            type="button"
            role="tab"
            id={tabId(section)}
            aria-selected={section === active}
            aria-controls={mounted.current.has(section) ? panelId(section) : undefined}
            tabIndex={section === active ? 0 : -1}
            onClick={() => onChange(section)}
            onKeyDown={(event) => moveFocus(event, index)}
            className={`px-4 py-1.5 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-300 light:focus-visible:outline-purple-600 ${
              section === active ? CHOSEN_TAB_CLASS : OTHER_TAB_CLASS
            }`}
          >
            {tabLabel}
          </button>
        ))}
      </div>
      {panels
        .filter(({ section }) => mounted.current.has(section))
        .map(({ section, render }) => (
          <div
            key={section}
            role="tabpanel"
            id={panelId(section)}
            aria-labelledby={tabId(section)}
            hidden={section !== active}
            tabIndex={0}
            className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-purple-300 light:focus-visible:outline-purple-600"
          >
            {render()}
          </div>
        ))}
    </>
  );
}
